import {
  Component, OnInit, OnDestroy, inject, HostListener,
  ElementRef, ViewChild, AfterViewInit, ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { map } from 'rxjs/operators';
import { StickyNote, StickyNotesService } from '@app/services/sticky-notes.service';
import { StickyNoteWindowComponent } from '../sticky-note-window/sticky-note-window.component';

const EDGE_KEY   = 'sticky-fab-edge';
const OFFSET_KEY = 'sticky-fab-offset';
const MARGIN     = 14;
const AUTO_CLOSE = 4000;
type Edge = 'top' | 'bottom' | 'left' | 'right';

@Component({
  selector: 'app-sticky-notes-button',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule, StickyNoteWindowComponent],
  template: `
    <div #fabRef class="sfab" [class.snapping]="snapping" [attr.data-edge]="edge">

      <!-- action buttons — absolutely positioned, float away from pill -->
      <button class="sfab-btn sfab-main" [class.vis]="open"
        [class.has-open]="hasOpen$ | async"
        (click)="toggleAll(); bump()"
        (mousedown)="$event.stopPropagation()"
        [matTooltip]="(hasOpen$ | async) ? 'Close all' : 'Open all'"
        [matTooltipPosition]="tipPos">
        <mat-icon>sticky_note_2</mat-icon>
        <span class="badge" *ngIf="count > 0">{{ count }}</span>
      </button>

      <button class="sfab-btn sfab-add" [class.vis]="open"
        (click)="createNote(); bump()"
        (mousedown)="$event.stopPropagation()"
        matTooltip="New note"
        [matTooltipPosition]="tipPos">
        <mat-icon>add</mat-icon>
      </button>

      <!-- pill — the only element in normal flow, defines container size -->
      <button class="sfab-pill"
        (mousedown)="onPillDown($event)"
        (click)="onPillClick()"
        matTooltip="Sticky notes"
        [matTooltipPosition]="tipPos">
        <mat-icon class="chev">expand_more</mat-icon>
        <span class="pill-badge" *ngIf="!open && count > 0">{{ count }}</span>
      </button>

    </div>

    <app-sticky-note-window
      *ngFor="let n of openNotes$ | async; trackBy: trackById"
      [note]="n">
    </app-sticky-note-window>
  `,
  styles: [`
    /* ── container — sized only by the pill ─── */
    .sfab {
      position: fixed;
      z-index: 9000;
      display: inline-block;  /* shrink-wraps the pill only */
      cursor: grab;
        isolation: isolate;  // ← add this
      &:active { cursor: grabbing; }
      &.snapping {
        transition:
          left   .3s cubic-bezier(.34,1.2,.64,1),
          top    .3s cubic-bezier(.34,1.2,.64,1),
          right  .3s cubic-bezier(.34,1.2,.64,1),
          bottom .3s cubic-bezier(.34,1.2,.64,1);
      }
    }

    /* ── pill ────────────────────────────────── */
    .sfab-pill {
      position: relative;
      overflow: visible;
      background: var(--primary-color, #4CAF50);
      color: #fff;
      border: none;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: grab;
      transition:
        border-radius .22s cubic-bezier(.4,0,.2,1),
        width         .22s cubic-bezier(.4,0,.2,1),
        height        .22s cubic-bezier(.4,0,.2,1),
        padding       .22s;
      &:active { cursor: grabbing; }

      /* horizontal pill — top / bottom */
      .sfab[data-edge="top"] &,
      .sfab[data-edge="bottom"] & {
        height: 28px; width: auto;
        min-width: 28px; border-radius: 14px; padding: 0 10px;
        &.open { width: 42px; height: 42px; border-radius: 50%; padding: 0; }
      }

      /* vertical pill — left / right */
      .sfab[data-edge="left"] &,
      .sfab[data-edge="right"] & {
        width: 28px; height: auto;
        min-height: 28px; border-radius: 14px; padding: 6px 0;
        &.open { width: 42px; height: 42px; border-radius: 50%; padding: 0; }
      }
    }

    /* ── pill badge ──────────────────────────── */
    .pill-badge {
      position: absolute;
      top: -5px; right: -5px;
      min-width: 16px; height: 16px; padding: 0 3px;
      border-radius: 8px;
      background: #e53935; color: #fff;
      font-size: 9px; font-weight: 700;
      display: flex; align-items: center; justify-content: center;
      border: 2px solid var(--background-color, #fff);
      pointer-events: none;
      z-index: 1;
    }

    /* ── chevron ─────────────────────────────── */
    .chev {
      font-size: 18px; width: 18px; height: 18px; flex-shrink: 0;
      transition: transform .22s cubic-bezier(.4,0,.2,1);
      .sfab[data-edge="bottom"] .sfab-pill &      { transform: rotate(0deg); }
      .sfab[data-edge="bottom"] .sfab-pill.open & { transform: rotate(180deg); }
      .sfab[data-edge="top"]    .sfab-pill &      { transform: rotate(180deg); }
      .sfab[data-edge="top"]    .sfab-pill.open & { transform: rotate(0deg); }
      .sfab[data-edge="left"]   .sfab-pill &      { transform: rotate(-90deg); }
      .sfab[data-edge="left"]   .sfab-pill.open & { transform: rotate(90deg); }
      .sfab[data-edge="right"]  .sfab-pill &      { transform: rotate(90deg); }
      .sfab[data-edge="right"]  .sfab-pill.open & { transform: rotate(-90deg); }
    }

    /* ── action buttons — absolutely positioned ─
       They float OUTSIDE the container so they
       never block anything when hidden.          */
    .sfab-btn {
      position: absolute;
      border: none; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      color: #fff; border-radius: 50%;
      opacity: 0; pointer-events: none;
      transition: transform .2s cubic-bezier(.34,1.3,.64,1), opacity .16s;
      &:active { filter: brightness(.9); }

      /* ── bottom edge: buttons float upward ── */
      .sfab[data-edge="bottom"] & {
        left: 50%;
        &.sfab-main {
          bottom: calc(100% + 6px);
          transform: translateX(-50%) scale(0) translateY(8px);
          &.vis { transform: translateX(-50%) scale(1) translateY(0); }
        }
        &.sfab-add {
          bottom: calc(100% + 6px + 42px + 6px);
          transform: translateX(-50%) scale(0) translateY(8px);
          transition-delay: .04s;
          &.vis { transform: translateX(-50%) scale(1) translateY(0); }
        }
      }

      /* ── top edge: buttons float downward ─── */
      .sfab[data-edge="top"] & {
        left: 50%;
        &.sfab-main {
          top: calc(100% + 6px);
          transform: translateX(-50%) scale(0) translateY(-8px);
          &.vis { transform: translateX(-50%) scale(1) translateY(0); }
        }
        &.sfab-add {
          top: calc(100% + 6px + 42px + 6px);
          transform: translateX(-50%) scale(0) translateY(-8px);
          transition-delay: .04s;
          &.vis { transform: translateX(-50%) scale(1) translateY(0); }
        }
      }

      /* ── left edge: buttons float rightward ── */
      .sfab[data-edge="left"] & {
        top: 50%;
        &.sfab-main {
          left: calc(100% + 6px);
          transform: translateY(-50%) scale(0) translateX(-8px);
          &.vis { transform: translateY(-50%) scale(1) translateX(0); }
        }
        &.sfab-add {
          left: calc(100% + 6px + 42px + 6px);
          transform: translateY(-50%) scale(0) translateX(-8px);
          transition-delay: .04s;
          &.vis { transform: translateY(-50%) scale(1) translateX(0); }
        }
      }

      /* ── right edge: buttons float leftward ── */
      .sfab[data-edge="right"] & {
        top: 50%;
        &.sfab-main {
          right: calc(100% + 6px);
          transform: translateY(-50%) scale(0) translateX(8px);
          &.vis { transform: translateY(-50%) scale(1) translateX(0); }
        }
        &.sfab-add {
          right: calc(100% + 6px + 42px + 6px);
          transform: translateY(-50%) scale(0) translateX(8px);
          transition-delay: .04s;
          &.vis { transform: translateY(-50%) scale(1) translateX(0); }
        }
      }
    }

    .sfab-main {
      width: 42px; height: 42px;
      background: var(--primary-color, #4CAF50);
      position: absolute;  /* keeps absolute from sfab-btn */
      mat-icon { font-size: 22px; width: 22px; height: 22px; }
      &.has-open { background: var(--accent-color, #FF9800); }
      &.vis { opacity: 1; pointer-events: auto; }
    }

    .sfab-add {
      width: 34px; height: 34px;
      background: var(--surface-color, #fff);
      border: 1.5px solid var(--primary-color, #4CAF50) !important;
      color: var(--primary-color, #4CAF50);
      &.vis { opacity: 1; pointer-events: auto; }
      mat-icon { font-size: 20px; width: 20px; height: 20px; }
    }

    /* ── badge on main button ────────────────── */
    .badge {
      position: absolute; top: -2px; right: -2px;
      min-width: 16px; height: 16px; padding: 0 3px;
      border-radius: 8px;
      background: #e53935; color: #fff;
      font-size: 9px; font-weight: 700;
      display: flex; align-items: center; justify-content: center;
      border: 2px solid var(--background-color, #fff);
      pointer-events: none;
    }
  `],
})
export class StickyNotesButtonComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('fabRef') fabRef!: ElementRef<HTMLDivElement>;

  private svc = inject(StickyNotesService);
  private cdr = inject(ChangeDetectorRef);

  openNotes$ = this.svc.notes$.pipe(map(n => n.filter(x => x.is_open)));
  hasOpen$   = this.svc.notes$.pipe(map(n => n.some(x => x.is_open)));
  get count() { return this.svc.notes.length; }

  open     = false;
  snapping = false;
  edge: Edge = 'bottom';

  get tipPos(): 'above' | 'below' | 'left' | 'right' {
    const m: Record<Edge, any> = { right: 'above', left: 'above', top: 'left', bottom: 'left' };
    return m[this.edge];
  }

  private autoTimer: any = null;
  private dragging       = false;
  private moved          = false;
  private dragStart      = { mx: 0, my: 0, elL: 0, elT: 0 };
  private savedOffset    = 0.5;

  // ── lifecycle ──────────────────────────────────────────────

  ngOnInit() {
    this.svc.load().subscribe();
    const e = localStorage.getItem(EDGE_KEY);
    if (e && ['top','bottom','left','right'].includes(e)) this.edge = e as Edge;
    const o = localStorage.getItem(OFFSET_KEY);
    if (o) this.savedOffset = parseFloat(o);
  }

  ngAfterViewInit() { this.applyEdge(false); }
  ngOnDestroy()     { clearTimeout(this.autoTimer); }

  // ── open / close ───────────────────────────────────────────

  private setOpen(v: boolean) {
    this.open = v;
    this.fabRef?.nativeElement.querySelector('.sfab-pill')?.classList.toggle('open', v);
    clearTimeout(this.autoTimer);
    if (v) this.autoTimer = setTimeout(() => this.setOpen(false), AUTO_CLOSE);
  }

  bump() {
    clearTimeout(this.autoTimer);
    this.autoTimer = setTimeout(() => this.setOpen(false), AUTO_CLOSE);
  }

  onPillClick() {
    if (this.moved) return;
    this.setOpen(!this.open);
  }

  toggleAll() {
    const notes = this.svc.notes;
    if (!notes.length) { this.createNote(); return; }
    notes.every(n => n.is_open) ? this.svc.closeAll() : this.svc.openAll();
  }

  // ── drag ───────────────────────────────────────────────────

  onPillDown(e: MouseEvent) {
    this.dragging = true;
    this.moved    = false;
    const rect = this.fabRef.nativeElement.getBoundingClientRect();
    const el   = this.fabRef.nativeElement;
    el.style.transition = 'none';
    el.style.right = el.style.bottom = 'auto';
    el.style.left  = rect.left + 'px';
    el.style.top   = rect.top  + 'px';
    el.style.transform = '';
    this.dragStart = { mx: e.clientX, my: e.clientY, elL: rect.left, elT: rect.top };
    e.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onMove(e: MouseEvent) {
    if (!this.dragging) return;
    const dx = e.clientX - this.dragStart.mx;
    const dy = e.clientY - this.dragStart.my;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) this.moved = true;
    if (!this.moved) return;
    const el = this.fabRef.nativeElement;
    el.style.left = Math.max(0, Math.min(window.innerWidth  - el.offsetWidth,  this.dragStart.elL + dx)) + 'px';
    el.style.top  = Math.max(0, Math.min(window.innerHeight - el.offsetHeight, this.dragStart.elT + dy)) + 'px';
  }

  @HostListener('document:mouseup')
  onUp() {
    if (!this.dragging) return;
    this.dragging = false;
    if (!this.moved) return;
    this.setOpen(false);
    this.snapToNearestEdge();
  }

  // ── edge snap ──────────────────────────────────────────────

  private snapToNearestEdge() {
    const rect    = this.fabRef.nativeElement.getBoundingClientRect();
    const dTop    = rect.top;
    const dBottom = window.innerHeight - rect.bottom;
    const dLeft   = rect.left;
    const dRight  = window.innerWidth  - rect.right;
    const min     = Math.min(dTop, dBottom, dLeft, dRight);
    let newEdge: Edge = 'bottom';
    if (min === dTop)    newEdge = 'top';
    if (min === dBottom) newEdge = 'bottom';
    if (min === dLeft)   newEdge = 'left';
    if (min === dRight)  newEdge = 'right';

    if (newEdge === 'top' || newEdge === 'bottom') {
      this.savedOffset = (rect.left + rect.width  / 2) / window.innerWidth;
    } else {
      this.savedOffset = (rect.top  + rect.height / 2) / window.innerHeight;
    }
    localStorage.setItem(OFFSET_KEY, String(this.savedOffset));
    this.edge = newEdge;
    localStorage.setItem(EDGE_KEY, newEdge);
    this.applyEdge(true);
    this.cdr.detectChanges();
  }

  private applyEdge(animate: boolean) {
    const el = this.fabRef?.nativeElement;
    if (!el) return;
    el.style.transition = animate ? '' : 'none';
    el.style.transform  = '';
    el.style.left = el.style.right = el.style.top = el.style.bottom = 'auto';
    const fw = el.offsetWidth, fh = el.offsetHeight, m = MARGIN;

    if (this.edge === 'bottom') {
      el.style.bottom = m + 'px';
      el.style.left   = Math.max(m, Math.min(window.innerWidth  - fw - m, this.savedOffset * window.innerWidth  - fw / 2)) + 'px';
    } else if (this.edge === 'top') {
      el.style.top  = m + 'px';
      el.style.left = Math.max(m, Math.min(window.innerWidth  - fw - m, this.savedOffset * window.innerWidth  - fw / 2)) + 'px';
    } else if (this.edge === 'left') {
      el.style.left = m + 'px';
      el.style.top  = Math.max(m, Math.min(window.innerHeight - fh - m, this.savedOffset * window.innerHeight - fh / 2)) + 'px';
    } else {
      el.style.right = m + 'px';
      el.style.top   = Math.max(m, Math.min(window.innerHeight - fh - m, this.savedOffset * window.innerHeight - fh / 2)) + 'px';
    }

    if (animate) {
      this.snapping = true;
      setTimeout(() => { this.snapping = false; this.cdr.detectChanges(); }, 380);
    }
  }

  createNote() {
    this.svc.create().subscribe({ error: e => console.error('[StickyNotes]', e) });
  }

  trackById(_: number, n: StickyNote) { return n.id; }
}